import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { formatCustomDate, formatDateToLexicographicalOrder, updateYAxisTitle } from '../AnalyticsHelper';
import { DEFAULT_HEIGHT, PT2H } from '../AnalyticsConstants';
import moment from 'moment';
import CompatioLoader from 'Components/Common/components/CompatioLoader';

type ColumnChartProps = {
  data: {
    name: string;
    data: {
      x: string;
      y: number;
    }[];
  }[];
  metricName?: string;
  xAxis?: string[];
  granularity?: string;
  timezone: string;
}

const ColumnChart = React.memo(({ data, metricName, xAxis, granularity, timezone }: ColumnChartProps) => {
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    setIsLoading(true);
    
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 10);

    return () => clearTimeout(timer);
  }, [data, xAxis]);

  const chartOptions: any = React.useMemo(() => ({
    chart: { 
      toolbar: { show: false },
    },
    xaxis: { 
      type: 'category', 
      categories: xAxis,
      labels: { 
        show: true,
        formatter: function (value:any) {
          const date = new Date(value);
          if (granularity === PT2H) {
            return formatCustomDate(value);
          } else {
            return formatDateToLexicographicalOrder(date, timezone);
          }
        },
      }, 
    },
    colors: [],
    plotOptions: { 
      bar: { 
        borderRadius: 0, 
        columnWidth: '40%', 
        dataLabels: { position: 'top' }, 
      }, 
    },
    yaxis: { 
      opposite: false, 
      title: { 
        text: updateYAxisTitle(metricName ?? ""), 
        rotate: -90, 
        offsetX: 3, 
        offsetY: 0, 
        style: { 
          color: '#8C919A', 
          fontSize: '11px', 
          fontFamily: 'Noto Sans, sans-serif', 
          fontWeight: 700, 
          cssClass: 'apexcharts-yaxis-title', 
        }, 
      }, 
    },
    dataLabels: { 
      enabled: true, 
      formatter: function (val:any) { return val + "%"; }, 
      offsetY: -20, 
      style: { fontSize: '8px', colors: ["transparent"] } 
    },
  }), [granularity, metricName, xAxis, timezone]);

  const series = React.useMemo(() => {
    return data.map((entry, index) => ({
      name: entry.name,
      data: xAxis?.map((dateString) => {
        const categoryDate = moment.utc(dateString);
        const dataPoint = entry.data.find((point) => {
          const pointDate = moment.utc(point.x);
          return pointDate.isSame(categoryDate);
        });
        return dataPoint ? dataPoint.y : null;
      }),
      strokeDashArray: index === 0 ? undefined : '5',
    }));
  }, [data, xAxis]);

  if (isLoading) {
    return (
      <div className='tw-justify-center tw-flex tw-text-center tw-items-center' style={{ height: DEFAULT_HEIGHT }}>
        <CompatioLoader />
      </div>
    );
  }

  return (
    <div style={{ height: DEFAULT_HEIGHT }}>
      <ReactApexChart
        options={chartOptions}
        series={series as any}
        type="bar"
        height={DEFAULT_HEIGHT}
        width="100%"
      />
    </div>
  );
});

ColumnChart.displayName = 'ColumnChart';

export default ColumnChart;